import React, { useState, useEffect, useRef, useCallback } from 'react';
import swal from '@sweetalert/with-react';

import { filterArrayOfObjects, useDebouncedValue, deleteData, loadSpecificData } from '../../../js/Helpers';
import { saveDataToDatabase } from '../../../js/SaveFunctions';

import SortToggle from '../../SortToggle';
import EditClassroomData from '../modals/edit_classroom';

import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faSearch, faEdit, faPlus } from '@fortawesome/free-solid-svg-icons';
import { faTrashAlt } from '@fortawesome/free-regular-svg-icons';

const buildClassroomDraft = (classroom_info = {}) => ({
	classroom_id: classroom_info.classroom_id,
	classroom_name: classroom_info.classroom_name || '',
});

const mergeSavedClassroomIntoList = (classroom_list = [], old_classroom_id, saved_classroom) => {
	const old_classroom_id_string = `${old_classroom_id}`;
	const new_classroom_id_string = `${saved_classroom.classroom_id}`;
	const original_classroom_index = classroom_list.findIndex((classroom_info) => `${classroom_info.classroom_id}` === old_classroom_id_string);

	if(old_classroom_id_string === new_classroom_id_string)
	{
		return classroom_list.map((classroom_info) => (
			`${classroom_info.classroom_id}` === old_classroom_id_string ? saved_classroom : classroom_info
		));
	}

	const next_classroom_list = classroom_list.filter((classroom_info) => (
		`${classroom_info.classroom_id}` !== old_classroom_id_string && `${classroom_info.classroom_id}` !== new_classroom_id_string
	));

	if(original_classroom_index === -1) return [...next_classroom_list, saved_classroom];

	const safe_insert_index = Math.min(original_classroom_index, next_classroom_list.length);
	return [
		...next_classroom_list.slice(0, safe_insert_index),
		saved_classroom,
		...next_classroom_list.slice(safe_insert_index),
	];
};

const classroomsMatch = (classroom_a = {}, classroom_b = {}) => {
	const normalized_a = buildClassroomDraft(classroom_a);
	const normalized_b = buildClassroomDraft(classroom_b);

	return normalized_a.classroom_name.trim() === normalized_b.classroom_name.trim();
};

export default function DatabaseDataClassrooms({ schoolInfo, databaseData, setDatabaseData, scheduleInfo, teachers, courses, dataIsLoading, blocks, handlePullNewData}) {
	const [isLoading, setIsLoading] = useState(true);
	const [handleChangeComplete, setHandleChangeComplete] = useState(false);

	const [filteredResults, setFilteredResults] = useState([]);
	const [displayResults, setDisplayResults] = useState([]);
	const [isSearching, setIsSearching] = useState(false);
	const [searchValue, setSearchValue] = useState('');
	const debouncedSearchValue = useDebouncedValue(searchValue, 120);
	
	const [showEditDataModal, setShowEditDataModal] = useState(false);
	const [currentDataID, setCurrentDataID] = useState(null);
	
	const [dontShowDeletePopup, setDontShowDeletePopup] = useState(false);
	const [editingCell, setEditingCell] = useState(null);
	const [cellDraft, setCellDraft] = useState(null);
	const [isSavingCell, setIsSavingCell] = useState(false);

	const filteredResultsRef = useRef(filteredResults);
	const classroomNameInputRef = useRef(null);
	filteredResultsRef.current = filteredResults;
	
	const handleIsSearching = (e) => {
		if(e.keyCode !== 8)
		{
			setIsLoading(false);
			setIsSearching(true);
		}
		else
		{
			setIsLoading(true);
			setIsSearching(false);
		}
	};
	
	const handleChange = useCallback(() => {
		if(filteredResultsRef.current.length === 0 && dataIsLoading) return;
		const search_bar = document.getElementsByClassName('school-data-search-bar')[0];
		const search_value = search_bar ? search_bar.value : '';
		
		if(search_value.length > 1)
		{
			const filtered_results = filterArrayOfObjects(filteredResultsRef.current, search_value, ['classroom_name']);
			setDisplayResults([...filtered_results]);
		}
		else
		{
			setDisplayResults([...filteredResultsRef.current]);
		}
		
		setIsLoading(false);
		setIsSearching(false);
		setHandleChangeComplete(true);
	}, [dataIsLoading]);
	
	useEffect(() => {
		handleChange();
	}, [debouncedSearchValue, handleChange]);

	const isEditingField = editingCell === 'classroom_name';

	const cancelCellEdit = useCallback(() => {
		setEditingCell(null);
		setCellDraft(null);
		setIsSavingCell(false);
	}, []);

	const startCellEdit = useCallback((classroom_info) => {
		if(!classroom_info) return;

		setEditingCell('classroom_name');
		setCellDraft(buildClassroomDraft(classroom_info));
		setIsSavingCell(false);
	}, []);

	useEffect(() => {
		if(!isEditingField) return;
		if(classroomNameInputRef.current)
		{
			classroomNameInputRef.current.focus();
			classroomNameInputRef.current.select();
		}
	}, [isEditingField]);

	const handleSaveCell = useCallback(async (classroom_info) => {
		if(!cellDraft || !editingCell || isSavingCell) return;

		const trimmed_classroom_name = cellDraft.classroom_name ? cellDraft.classroom_name.trim() : '';
		const draft_to_compare = {
			...classroom_info,
			classroom_name: trimmed_classroom_name,
		};

		if(classroomsMatch(classroom_info, draft_to_compare))
		{
			cancelCellEdit();
			return;
		}

		if(!trimmed_classroom_name)
		{
			swal({
				title: 'Missing a Classroom Name',
				icon:'warning',
				buttons: {
					cancel: {
						text: 'Okay',
						value: false,
						visible: true,
						className: 'blue-btn'
					}
				},
				content: (
					<div>
						<p>You need to enter a classroom name before saving!</p>
					</div>
				)
			});
			return;
		}

		setIsSavingCell(true);

		const original_classroom_id = classroom_info.classroom_id;
		const full_classroom = await loadSpecificData({
			data_type:'classroom',
			data_id:original_classroom_id,
			data_version_id:schoolInfo.current_data_version_id,
			schedule_version_id:schoolInfo.current_schedule_version_id,
			school_id:schoolInfo.school_id,
			database:'elementary'
		});

		if(!full_classroom || full_classroom.msg)
		{
			setIsSavingCell(false);
			swal({
				title: 'Unable to Save',
				icon:'warning',
				buttons: {
					cancel: {
						text: 'Okay',
						value: false,
						visible: true,
						className: 'blue-btn'
					}
				},
				content: <div><p>We couldn&apos;t load this classroom&apos;s latest data. Please try again.</p></div>
			});
			return;
		}

		const classroom_to_save = {
			...full_classroom,
			classroom_id: original_classroom_id,
			classroom_name: trimmed_classroom_name,
		};

		const response_data = await saveDataToDatabase({
			school_id:schoolInfo.school_id,
			schedule_version_id:schoolInfo.current_schedule_version_id,
			data_type:'classroom',
			data_to_save:classroom_to_save,
			database:'elementary'
		});

		if(!response_data?.new_data_id)
		{
			setIsSavingCell(false);
			swal({
				title: 'Unable to Save',
				icon:'warning',
				buttons: {
					cancel: {
						text: 'Okay',
						value: false,
						visible: true,
						className: 'blue-btn'
					}
				},
				content: <div><p>We couldn&apos;t save this classroom value. Please try again.</p></div>
			});
			return;
		}

		const saved_classroom_id = response_data.new_data_id;
		let saved_classroom = {
			...classroom_info,
			...classroom_to_save,
			classroom_id: saved_classroom_id,
		};

		const refreshed_classroom = await loadSpecificData({
			data_type:'classroom',
			data_id:saved_classroom_id,
			data_version_id:schoolInfo.current_data_version_id,
			schedule_version_id:schoolInfo.current_schedule_version_id,
			school_id:schoolInfo.school_id,
			database:'elementary'
		});

		if(refreshed_classroom && !refreshed_classroom.msg) saved_classroom = refreshed_classroom;

		const next_database_data = mergeSavedClassroomIntoList(databaseData, original_classroom_id, saved_classroom);
		const next_filtered_results = mergeSavedClassroomIntoList(filteredResults, original_classroom_id, saved_classroom);

		setDatabaseData(next_database_data);
		setFilteredResults(next_filtered_results);
		cancelCellEdit();
	}, [cellDraft, editingCell, isSavingCell, schoolInfo, databaseData, filteredResults, setDatabaseData, cancelCellEdit]);

	const handleCellBlur = (classroom_info) => {
		if(!isEditingField || isSavingCell) return;
		handleSaveCell(classroom_info);
	};

	const handleCellKeyDown = async (event, classroom_info) => {
		if(event.key === 'Enter')
		{
			event.preventDefault();
			await handleSaveCell(classroom_info);
		}
		else if(event.key === 'Escape')
		{
			event.preventDefault();
			cancelCellEdit();
		}
	};

	const handleClassroomNameChange = (event) => {
		const { value } = event.target;
		setCellDraft((current_draft) => ({...current_draft, classroom_name: value}));
	};

	const handleSortClick = (sortedData) => {
		cancelCellEdit();
		setDisplayResults([...sortedData]);
	};
	
	const toggleEditData = (dataID) => {
		cancelCellEdit();
		if(showEditDataModal)
		{
			setShowEditDataModal(false);
			return;
		}

		setCurrentDataID(dataID);
		setShowEditDataModal(true);
	};
	
	const toggleDontShow = () => {
		setDontShowDeletePopup(!dontShowDeletePopup);
	};
	
	const handeDeleteData = async (data_id) => {
		const data = {school_id:schoolInfo.school_id, schedule_version_id:schoolInfo.current_schedule_version_id, data_type:'classroom', data_ids:[data_id], database:'elementary'};
		let delete_data_bool = true;
		
		const data_index = databaseData.findIndex((classroom) => `${classroom.classroom_id}` === `${data_id}`);
		const filtered_results_index = filteredResults.findIndex((classroom) => `${classroom.classroom_id}` === `${data_id}`);
		const data_info = databaseData[data_index];
		
		if(!dontShowDeletePopup)
		{
			const options =  {
				title: 'Are you sure?',
				icon: 'warning',
				dangerMode: true,
				buttons: {
					cancel: {
						text: 'Cancel',
						value: false,
						visible: true,
						className: 'gray-btn'
					},
					confirm: {
						text: 'Yes',
						value: true,
						visible: true,
						className: 'red-btn'
					},
				},
				content: (
					<div>
						<div>
							<p>Do you really want to delete {data_info.classroom_name}?</p>
							<p className='red-text'>This will <strong>permanently</strong> remove it from the database.</p>
						</div>
						<div className='sweet-alert-dont-show-message' onClick={() => toggleDontShow('remove')}>
							<label className="checkmark_container">Please don&rsquo;t show me this message again
								<input type="checkbox" />
								<span className="checkmark"></span>
							</label>
						</div>
					</div>
				)
			};
		
			delete_data_bool = await swal(options);
		}
		
		if(delete_data_bool)
		{
			cancelCellEdit();
			databaseData.splice(data_index, 1);
			filteredResults.splice(filtered_results_index, 1);

			setDatabaseData([...databaseData]);
			setFilteredResults([...filteredResults]);
			
			deleteData(data);
		}
	};
	
	useEffect(() => {
		if(!showEditDataModal) setCurrentDataID(null);

		const search_bar = document.getElementsByClassName('school-data-search-bar')[0];
		const search_value = search_bar ? search_bar.value : '';
		if(search_value.length > 0) handleChange();
	}, [showEditDataModal, handleChange]);
	
	useEffect(() => {
		handleChange();
	}, [filteredResults, handleChange]);

	useEffect(() => {
		if(!handleChangeComplete) setFilteredResults([...databaseData]);
	}, [databaseData, dataIsLoading, handleChangeComplete]);
	
	const row_style = {gridTemplateColumns:'1fr 70px'};
	
	return (
		<>
		{showEditDataModal &&
			<EditClassroomData schoolInfo={schoolInfo} dataID={currentDataID} toggleEditData={toggleEditData} scheduleInfo={scheduleInfo} classrooms={databaseData} courses={courses} teachers={teachers} setDatabaseData={setDatabaseData} filteredResults={filteredResults} setFilteredResults={setFilteredResults} blocks={blocks}/>
		}
		<div className='school-data-content-screen'>
			<h1 className='school-data-main-heading capitalize'>Classrooms</h1>
			<div className='school-data-top-bar'>
				<div className='school-data-search-bar-container'>
					<input className='school-data-search-bar' placeholder='Search for classrooms...' value={searchValue} onChange={(e) => setSearchValue(e.target.value)} onKeyDown={(e) => handleIsSearching(e)} />
					<FontAwesomeIcon className='school-data-search-bar-icon' icon={faSearch}/>
				</div>
			</div>
			<div className='school-data-database-display'>
				<div className='school-data-database-row school-data-database-display-header fixed-heading-on-scroll' style={row_style}>
					<div className='school-data-database-header-col'>Classroom Name <SortToggle handleOnClick={handleSortClick} sortKey='classroom_name' passedData={displayResults}/></div>
					<div className='school-data-database-add-new-container' onClick={() => toggleEditData(null)}>
						<div className='school-data-database-add-new-btn'>
							<FontAwesomeIcon className='white-text' icon={faPlus}/>
						</div>
					</div>
				</div>
				{(!handleChangeComplete && isLoading) ?
				(
					<div className='school-data-database-message-container'>
						<img src={require('../../../images/balls.gif')} alt='loading...' style={{height:'80px'}}/>
					</div>
				): isSearching ?
				(
					<div className='school-data-database-message-container'>
						<img src={require('../../../images/searching.gif')} alt='searching...' style={{height:'80px'}}/>
					</div>
				):
				(
					<>
					{displayResults.length === 0 &&
						<div className='school-data-database-message-container'>
							<h4>Sorry, no results were found!</h4>
						</div>
					}
					{displayResults.map((data_info) => {
						return (
							<div className='school-data-database-row' style={row_style} key={data_info.classroom_id}>
								<div
									className={isEditingField && `${cellDraft?.classroom_id}` === `${data_info.classroom_id}` ? 'school-data-inline-editor-container' : 'school-data-inline-editable-cell'}
									onClick={!editingCell ? () => startCellEdit(data_info) : undefined}
								>
									{isEditingField && `${cellDraft?.classroom_id}` === `${data_info.classroom_id}` ?
										(isSavingCell ?
											<div className='school-data-inline-saving-cell'>
												<img src={require('../../../images/balls.gif')} alt='saving...' className='school-data-inline-saving-gif'/>
											</div>
										:
												<input
													ref={classroomNameInputRef}
													className='school-data-inline-edit-input'
													value={cellDraft?.classroom_name || ''}
													disabled={isSavingCell}
													onChange={handleClassroomNameChange}
													onKeyDown={(e) => handleCellKeyDown(e, data_info)}
													onBlur={() => handleCellBlur(data_info)}
												/>
										)
									:
										<div>{data_info.classroom_name}</div>
									}
								</div>
								<div className='school-data-database-row-btn-container'>
									<FontAwesomeIcon className='gray-to-dark-blue-link' icon={faEdit} onClick={() => toggleEditData(data_info.classroom_id)}/>
									<FontAwesomeIcon className='gray-to-red-link' icon={faTrashAlt} onClick={() => handeDeleteData(data_info.classroom_id)}/>
								</div>
							</div>
						);
					})}
					</>
				)}
			</div>
		</div>
		</>
	);
}
